import React from 'react';
import { Box, Typography, Card, CardContent, Stack, Chip, Alert, Button } from '@mui/joy';
import { useProfile } from '../hooks/useProfile';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';

const ProfileTest: React.FC = () => {
  const { user } = useSupabaseAuth();
  const { 
    profile, 
    loading, 
    error, 
    isStaff, 
    isAdmin, 
    isCustomer, 
    canAccessAdmin, 
    canAccessApp,
    refreshProfile 
  } = useProfile();

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Loading profile...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert color="danger" sx={{ mb: 2 }}>
          Error loading profile: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
      <Typography level="h2" mb={3}>Profile & Access Control Test</Typography>
      
      <Stack spacing={3}>
        {/* User Info */}
        <Card>
          <CardContent>
            <Typography level="h4" mb={2}>User Information</Typography>
            <Stack spacing={1}>
              <Typography><strong>Email:</strong> {user?.email}</Typography>
              <Typography><strong>User ID:</strong> {user?.id}</Typography>
            </Stack>
          </CardContent>
        </Card>

        {/* Profile Info */}
        <Card>
          <CardContent>
            <Typography level="h4" mb={2}>Profile Information</Typography>
            {profile ? (
                          <Stack spacing={1}>
              <Typography><strong>Full Name:</strong> {profile.full_name || 'Not set'}</Typography>
              <Typography><strong>Email:</strong> {profile.email || 'Not set'}</Typography>
              <Typography><strong>Phone:</strong> {profile.phone || 'Not set'}</Typography>
                <Typography><strong>Group:</strong> 
                  <Chip 
                    color={profile.group === 'staff' ? 'success' : 'neutral'} 
                    size="sm" 
                    sx={{ ml: 1 }}
                  >
                    {profile.group}
                  </Chip>
                </Typography>
                <Typography><strong>Role:</strong> 
                  {profile.role ? (
                    <Chip 
                      color={profile.role === 'admin' ? 'danger' : 'primary'} 
                      size="sm" 
                      sx={{ ml: 1 }}
                    >
                      {profile.role}
                    </Chip>
                  ) : (
                    <Chip color="neutral" size="sm" sx={{ ml: 1 }}>
                      None
                    </Chip>
                  )}
                </Typography>
              </Stack>
            ) : (
              <Typography color="danger">No profile found</Typography>
            )}
          </CardContent>
        </Card>

        {/* Access Control Status */}
        <Card>
          <CardContent>
            <Typography level="h4" mb={2}>Access Control Status</Typography>
            <Stack spacing={2}>
              <Box>
                <Typography level="title-md" mb={1}>User Type:</Typography>
                <Stack direction="row" spacing={1}>
                  <Chip color={isStaff ? 'success' : 'neutral'} variant={isStaff ? 'solid' : 'outlined'}>
                    Staff
                  </Chip>
                  <Chip color={isAdmin ? 'danger' : 'neutral'} variant={isAdmin ? 'solid' : 'outlined'}>
                    Admin
                  </Chip>
                  <Chip color={isCustomer ? 'neutral' : 'neutral'} variant={isCustomer ? 'solid' : 'outlined'}>
                    Customer
                  </Chip>
                </Stack>
              </Box>
              
              <Box>
                <Typography level="title-md" mb={1}>Permissions:</Typography>
                <Stack direction="row" spacing={1}>
                  <Chip 
                    color={canAccessApp ? 'success' : 'danger'} 
                    variant="solid"
                  >
                    Can Access App: {canAccessApp ? 'Yes' : 'No'}
                  </Chip>
                  <Chip 
                    color={canAccessAdmin ? 'danger' : 'neutral'} 
                    variant={canAccessAdmin ? 'solid' : 'outlined'}
                  >
                    Can Access Admin: {canAccessAdmin ? 'Yes' : 'No'}
                  </Chip>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Test Actions */}
        <Card>
          <CardContent>
            <Typography level="h4" mb={2}>Test Actions</Typography>
            <Stack direction="row" spacing={2}>
              <Button 
                variant="outlined" 
                onClick={refreshProfile}
              >
                Refresh Profile
              </Button>
              <Button 
                variant="outlined" 
                component="a" 
                href="/admin"
                disabled={!canAccessAdmin}
              >
                Try Admin Panel
              </Button>
              <Button 
                variant="outlined" 
                component="a" 
                href="/projects"
              >
                Go to Projects
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Access Warnings */}
        {!canAccessApp && (
          <Alert color="danger">
            <Typography level="title-md">Access Restricted</Typography>
            <Typography>
              You do not have permission to access this application. Only staff members can access the Jarvis application.
            </Typography>
          </Alert>
        )}

        {canAccessApp && !canAccessAdmin && (
          <Alert color="warning">
            <Typography level="title-md">Limited Access</Typography>
            <Typography>
              You can access the application but cannot access the admin panel. Contact an administrator if you need admin privileges.
            </Typography>
          </Alert>
        )}

        {canAccessAdmin && (
          <Alert color="success">
            <Typography level="title-md">Full Access</Typography>
            <Typography>
              You have full access to the application including the admin panel.
            </Typography>
          </Alert>
        )}
      </Stack>
    </Box>
  );
};

export default ProfileTest; 